"use client";

import { useEffect, useRef, useState } from "react";

/**
 * SEO- und Hydration-sichere Count-Up-Zahl.
 *
 * Kernprinzip gegen SEO-Schaden:
 *   • Der initiale State ist der ECHTE Zielwert (nicht 0). Damit enthält
 *     sowohl das Server-HTML als auch der erste Client-Render die finale
 *     Zahl – Google/Bing/AI-Crawler sehen „50+", niemals „0+".
 *   • Erst NACH der Hydration (useEffect) setzen wir – falls im Viewport
 *     und ohne prefers-reduced-motion – kurz auf 0 und animieren hoch.
 *   • Kein Hydration-Mismatch, weil Server und erster Client-Paint
 *     identisch (= Zielwert) sind.
 *
 * Performance: nur ein IntersectionObserver + requestAnimationFrame,
 * stoppt sich nach einem Durchlauf selbst (once). Kein Layout-Thrash,
 * tabular-nums verhindert Breiten-Springen.
 */
export function CountUp({
  to,
  durationMs = 1600,
  decimals = 0,
  prefix = "",
  suffix = "",
  className,
}: {
  to: number;
  durationMs?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  // Startwert = Zielwert → SSR/erste Hydration zeigen die echte Zahl.
  const [value, setValue] = useState(to);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reduced-Motion respektieren: gar nicht animieren, Zielwert bleibt.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    let raf = 0;
    let started = false;

    const animate = () => {
      const start = performance.now();
      const from = 0;
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / durationMs);
        // easeOutCubic – schnell anlaufen, sanft auslaufen
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(from + (to - from) * eased);
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          started = true;
          setValue(0); // kurz auf 0, dann hochzählen
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [to, durationMs]);

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className} style={{ fontVariantNumeric: "tabular-nums" }}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
